import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from 'LuxKit/Typography/Typography'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import type { SupportedLoyaltyActionReward } from 'loyalty/constants/loyaltyActionRewards'
import { rem } from 'polished'
import { useCallback, type MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import LoyaltyActionRewardsIcon from './LoyaltyActionRewardsIcon'
import LoyaltyActionRewardsStatusIndicator from './LoyaltyActionRewardsStatusIndicator'

const ListItemRow = styled(Group)`
  padding-block: ${rem(18)};
  padding-inline: 0 ${rem(4)};
`

const CopyColumn = styled(Group)`
  min-width: 0;
`

/** Circular grey well for the action icon — same Sheet + round pattern as the status indicator. */
const IconCircle = styled(Sheet)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  flex-shrink: 0;
  box-sizing: border-box;
`

const TitleTooltip = styled(Tooltip)`
  display: inline-flex;
  align-items: center;
  height: var(${TYPOGRAPHY_LINE_HEIGHT_CSS_VAR});
  margin-inline-start: ${rem(4)};
  vertical-align: bottom;
`

interface Props {
  reward: SupportedLoyaltyActionReward
  tier: App.LoyaltyTier
  title: string
  description?: string
  showChevron: boolean
}

/** Skeleton mirroring a quest row while action rewards load. */
export function LoyaltyActionRewardsListItemContentLoadingSkeleton() {
  return (
    <ListItemRow direction="horizontal" gap={16} verticalAlign="center" fullWidth>
      <LoadingBox width={48} height={48} borderRadius="round" />
      <CopyColumn direction="vertical" gap={8} grow={1}>
        <TextLoadingBox typography="body-medium" width="60%" />
        <TextLoadingBox typography="body-medium" width="40%" />
      </CopyColumn>
      <LoadingBox width={40} height={40} borderRadius="round" />
    </ListItemRow>
  )
}

function LoyaltyActionRewardsListItemContent({ reward, tier, title, description, showChevron }: Props) {
  const { isComplete, earnEstimate, appliedRecords } = reward
  const itemColour = isComplete ? 'neutral-four' : 'neutral-one'
  const preventRowActivate = useCallback((event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }, [])

  const estimateStatusCredits = earnEstimate
    ? earnEstimate.statusCredits + earnEstimate.bonusStatusCredits
    : undefined
  const earned = isComplete
    ? appliedRecords.reduce(
        (totals, record) => ({
          bonusPoints: totals.bonusPoints + (record.bonusPoints ?? 0),
          statusCredits: totals.statusCredits + (record.statusCredits ?? 0) + (record.bonusStatusCredits ?? 0),
        }),
        { bonusPoints: 0, statusCredits: 0 },
      )
    : undefined
  const hasEarnedPoints = !!earned && earned.bonusPoints > 0
  const hasEarnedStatusCredits = !!earned && earned.statusCredits > 0

  return (
    <ListItemRow direction="horizontal" gap={16} verticalAlign="center" fullWidth>
      <IconCircle background="neutral-seven" borderRadius="round">
        <LoyaltyActionRewardsIcon
          actionName={reward.actionName}
          colour={itemColour}
        />
      </IconCircle>
      <CopyColumn direction="vertical" gap={8} grow={1}>
        <BodyText
          variant="medium"
          weight={isComplete ? 'normal' : 'bold'}
          colour={itemColour}
        >
          {title}
          {description && (
            <TitleTooltip
              description={description}
              placement="bottom"
            >
              <LineInfoCircleIcon
                size="XS"
                colour={itemColour}
                onClick={preventRowActivate}
              />
            </TitleTooltip>
          )}
        </BodyText>
        {isComplete && earned && (hasEarnedPoints || hasEarnedStatusCredits) && (
          <BodyTextBlock
            variant="small"
            colour="neutral-four"
            weight="bold"
            align="start"
            startIcon={<LoyaltyTierIcon
              size="S"
              tier={tier}
            />}
          >
            {hasEarnedPoints && hasEarnedStatusCredits && (
              <FormattedMessage
                id="J16pcYSfC25Y9p12Jigg"
                defaultMessage="{points} + {statusCredits} added"
                description="Muted label on a completed Société activity row showing earned points and status credits from applied action-reward records"
                values={{
                  points: <FormatLoyaltyPoints abbreviated points={earned.bonusPoints} />,
                  statusCredits: <FormatLoyaltyStatusCredits statusCredits={earned.statusCredits} />,
                }}
              />
            )}
            {hasEarnedPoints && !hasEarnedStatusCredits && (
              <FormattedMessage
                id="8ih35YdoVayBRJdFWGGH"
                defaultMessage="{points} added"
                description="Muted label on a completed Société activity row showing earned points only. {points} is a formatted number with its localized points unit, such as 5 pts; do not add another points unit."
                values={{
                  points: <FormatLoyaltyPoints abbreviated points={earned.bonusPoints} />,
                }}
              />
            )}
            {!hasEarnedPoints && hasEarnedStatusCredits && (
              <FormattedMessage
                id="8gP_DB7F1UNUtDXmZkEP"
                defaultMessage="{statusCredits} added"
                description="Muted label on a completed Société activity row showing earned status credits only from applied action-reward records"
                values={{
                  statusCredits: <FormatLoyaltyStatusCredits statusCredits={earned.statusCredits} />,
                }}
              />
            )}
          </BodyTextBlock>
        )}
        {!isComplete && earnEstimate && (
          <PriceRowLoyaltyPoints
            tier={tier}
            size="S"
            horizontalAlign="start"
            earnablePoints={earnEstimate.bonusPoints}
            statusCredits={estimateStatusCredits}
            calculationType="exact"
          />
        )}
      </CopyColumn>
      <Group direction="horizontal" gap={4} verticalAlign="center">
        {showChevron && (
          <DropdownChevron
            direction="right"
            size="S"
            colour={itemColour}
          />
        )}
        <LoyaltyActionRewardsStatusIndicator reward={reward} tier={tier} />
      </Group>
    </ListItemRow>
  )
}

export default LoyaltyActionRewardsListItemContent
